/**
 * ShareUserBubble — per-type subcomponent for `ChatMessageType.shareUser`.
 *
 * Flutter source: `currentUserMessageTypeShareUser` (`chat_screen.dart:1917-2085`)
 * / `peerUserMessageTypeShareUser` (`:3044-3212`). Variants are structurally
 * identical; the only differences are row order (own:
 * `[icon][peer-info]`, peer: `[peer-info][icon]`) and the corner-collapse
 * direction handled by `getBubbleCornerRadii`.
 *
 * Co-located subcomponents (each carries its own Flutter anchors and
 * theme/responsive mappings); all but the identity block are shared with
 * `ShareContentBubble`:
 *  - `ShareBubbleRow`         — the icon + payload row, incl. the row order.
 *  - `ShareUserIdentityBlock` — the peer-info bubble: avatar + name +
 *                               `@username`.
 *  - `ShareCaptionBubble`     — the optional reply-text bubble.
 *
 * Non-obvious behaviour:
 *  - Null `shareInfo` early-return on tap — no navigation, no timestamp
 *    toggle (`chat_screen.dart:1921-1923` / `:3048-3050`).
 *  - Tap order: navigate → toggle timestamp. This is the ONE non-stub tap
 *    in round one; every other bubble routes to
 *    `InfoSheet.showNotImplementedYet`. Mirrors Flutter
 *    `chat_screen.dart:1925-1940` / `:3052-3067`.
 *
 * Responsive / theme mappings are documented inline at each usage site.
 *
 * Deviations from plan:
 *  - `Navigator.navigateToUserDetails` is used instead of a router hook —
 *    the established project navigation contract.
 *
 * Container element: rendered as `<div role="button" tabIndex={0}>` with
 * `useEnterOrSpaceActivation` for keyboard parity — see `TextBubble.tsx`
 * for the rationale.
 */

import React from 'react';
import { ChatMessageType } from '@/domain/enums/chatMessageType';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { Navigator } from '@/presentation/routing/Navigator';
import { ChatUtils } from '@/domain/utils/chatUtils';
import {
  registerBubble,
  type BubbleSubcomponentProps,
} from '../../registry/bubbleRegistry';
import { useEnterOrSpaceActivation } from '@/presentation/hooks/useEnterOrSpaceActivation';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { ShareBubbleRow } from './ShareBubbleRow';
import { ShareUserIdentityBlock } from './ShareUserIdentityBlock';
import { ShareCaptionBubble } from './ShareCaptionBubble';

export const ShareUserBubble: React.FC<BubbleSubcomponentProps> = ({
  message,
  isOwn,
  isLastOfRun,
  onToggleTimestamp,
}) => {
  const responsive = useResponsiveDesign();

  const corners = ChatUtils.getBubbleCornerRadii({
    isOwn,
    isLastOfRun,
    runMidRadius: responsive.borderRadius.lg,
    runEndRadius: responsive.borderRadius.sm,
  });

  // Flutter early-returns from the tap handler when `shareInfo` is null
  // (`chat_screen.dart:1921-1923` / `:3048-3050`). The web port mirrors
  // exactly — no navigation, no timestamp toggle, but the bubble itself
  // still renders (defensive against null `usersInfo` inside
  // `ShareUserIdentityBlock`, since `shareInfo == null` should not happen
  // for a real shareUser message).
  const shareInfo = message.shareInfo;

  /**
   * Tap handler — mirrors Flutter at `chat_screen.dart:1920-1940` (own) /
   * `:3047-3067` (peer). Order matters:
   *   1. Bail on null `shareInfo`.
   *   2. Navigate to the shared user's details page.
   *   3. Fire `onToggleTimestamp` so the hook flips this message's entry
   *      (and resets every other entry to `false`).
   * Step (3) ships through the dispatcher's `onToggleTimestamp` contract;
   * the hook owns the "only one open at a time" invariant — see
   * `MessageBubble.tsx` for the contract.
   */
  const handleTap = (): void => {
    if (!shareInfo) {
      return;
    }
    Navigator.navigateToUserDetails(shareInfo.usersInfo.userId);
    onToggleTimestamp(message.timestamp);
  };

  const handleKeyDown = useEnterOrSpaceActivation<HTMLDivElement>(handleTap);

  return (
    <div
      {...qaAttr({ id: `chat-bubble-share-user-${message.documentId}` })}
      role="button"
      tabIndex={0}
      onClick={handleTap}
      onKeyDown={handleKeyDown}
      style={{
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        // Flutter's outer column uses `crossAxisAlignment.end` for own
        // (`chat_screen.dart:1945`) / `.start` for peer (`:3072`).
        alignItems: isOwn ? 'flex-end' : 'flex-start',
        maxWidth: '100%',
      }}
    >
      <ShareBubbleRow isOwn={isOwn}>
        <ShareUserIdentityBlock message={message} corners={corners} />
      </ShareBubbleRow>
      <ShareCaptionBubble message={message} corners={corners} isOwn={isOwn} />
    </div>
  );
};

// Register with the dispatcher so `MessageBubble` routes
// `ChatMessageType.shareUser` to this component. See `MessageBubble.tsx`
// for the registry contract.
registerBubble(ChatMessageType.shareUser, ShareUserBubble);
